/**
 * V4NodePalette — Phase 7d redesign.
 *
 * Tabbed-category palette replacing the original Phase-5b vertical
 * accordion. Per the operator feedback that the prior layout looked
 * "amateurish" — three problems addressed:
 *
 *   1. Vertical accordion forced scrolling between categories. Now a
 *      horizontal icon-only TAB STRIP lets the operator jump to any
 *      category in one click — only the active category's nodes show.
 *   2. Every row was 38px tall with a label + redundant type subtitle
 *      ("Webhook / webhook"). Now rows are 26px with just the label
 *      and a real per-type Lucide icon (not a 6px colored dot).
 *   3. Headers competed visually with items. Now there's a single
 *      uppercase letter-spaced category header in the body, calmly
 *      anchoring the active section without screaming for attention.
 *
 * Search behaviour:
 *   • Search box at the top, always visible.
 *   • Empty search → body shows the active tab's nodes (default tab: tasks).
 *   • Non-empty search → tab strip stays but the body shows MATCHES
 *     across ALL categories with their category label inline (so the
 *     operator can see what category a match belongs to).
 *
 * Click contract (only path now — drag-drop removed 2026-05-29 because
 * it kept producing orphan / disconnected nodes; see V4CanvasView for
 * the rationale):
 *   • CLICK: onAddOrphan(type) — routes through the host's smart
 *     cascade (after-selected → before-end → free with toast).
 *   • Single-instance entry types (Start sentinel) still hidden via
 *     hiddenTypes prop from the host.
 *
 * Tokens: brand teal accent on active tab + hover (BRAND.dark); stone
 * neutrals for chrome (S[50..900]); ACCENT for category color tints.
 */

import React, { useMemo, useState } from 'react';
import { NODE_CATEGORIES, NODE_TYPES } from '../../data/soarNodes';
import {
  // category icons
  Zap, PlayCircle, Shield, GitBranch, Lock, Database, Bot, ShieldCheck, Plug,
  // chrome
  ChevronsLeft, ChevronsRight, Search,
  // per-node icons (most common ones used in node defs)
  Globe, Clock, Brain, Bell, Webhook as WebhookIcon, Mail, AlertCircle,
  Cpu, Code, Server, Combine, FileSearch, Users, CheckCircle,
  CheckCircle2, Eye, Workflow, Repeat, RotateCw, MessageCircle,
  CheckSquare, Sparkles, Cog, Send, Search as SearchIcon, FileText,
  Activity, GitFork, Hash, Power, Box, Layers,
} from 'lucide-react';
import {
  BRAND, S, ACCENT, SPACING, RADIUS, FONT, WEIGHT,
} from './v4Tokens';

// Category icons map — keyed by the `icon: 'Zap'` string in NODE_CATEGORIES.
const CATEGORY_ICONS = {
  Zap, PlayCircle, Shield, GitBranch, Lock, Database, Bot, ShieldCheck, Plug,
};

// Per-node icons map — keyed by the `icon: 'Webhook'` string in node defs.
// Best-effort coverage of the icons referenced by NODE_TYPES; anything
// unmapped falls back to the category icon (and ultimately to PlayCircle).
const NODE_ICONS = {
  Webhook: WebhookIcon, Clock, Database, Bot, MessageCircle, Code, Bell,
  GitBranch, Shield, Plug, AlertCircle, Lock, Globe, Brain, Cpu, Server,
  Combine, FileSearch, Users, CheckCircle, CheckCircle2, Eye, Workflow,
  Repeat, RotateCw, CheckSquare, Sparkles, Cog, Send, FileText, Activity,
  GitFork, Hash, Power, Box, Layers, Zap, PlayCircle, ShieldCheck,
  Search: SearchIcon,
};

const PANEL_WIDTH = 240;
const COLLAPSED_WIDTH = 36;
const DEFAULT_TAB = 'tasks';

// ─── Hook: filter node list once per search/category change ──────
function useFilteredNodes(search, activeCatId, hiddenTypes) {
  return useMemo(() => {
    const q = search.trim().toLowerCase();
    const hideSet = new Set(hiddenTypes || []);
    const all = Object.values(NODE_TYPES).filter((def) => {
      if (!def || def.paletteHidden) return false;
      if (def.type === 'end') return false;
      if (hideSet.has(def.type)) return false;
      return true;
    });
    if (q) {
      // Search mode — match across ALL categories, attach category meta
      // so the body can group/label by category inline.
      const matches = all.filter((def) => {
        const hay = [
          def.type, def.label, def.description,
          Array.isArray(def.tags) ? def.tags.join(' ') : '',
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
      // Group by category id for inline category labels.
      const byCat = new Map();
      matches.forEach((def) => {
        const catId = def.category || 'tasks';
        if (!byCat.has(catId)) byCat.set(catId, []);
        byCat.get(catId).push(def);
      });
      return NODE_CATEGORIES
        .map((cat) => ({ category: cat, nodes: byCat.get(cat.id) || [] }))
        .filter((bucket) => bucket.nodes.length > 0);
    }
    // Tab mode — only the active category's nodes.
    const inCat = all.filter((def) => (def.category || 'tasks') === activeCatId);
    const cat = NODE_CATEGORIES.find((c) => c.id === activeCatId) || NODE_CATEGORIES[0];
    return inCat.length > 0 ? [{ category: cat, nodes: inCat }] : [];
  }, [search, activeCatId, hiddenTypes]);
}

export default function V4NodePalette({
  onAddOrphan,
  readOnly = false,
  hiddenTypes,
}) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [activeCatId, setActiveCatId] = useState(DEFAULT_TAB);

  const buckets = useFilteredNodes(search, activeCatId, hiddenTypes);

  // (Drag source removed 2026-05-29 — see header comment. Click is the
  // only add path now.)

  // ── COLLAPSED RAIL ─────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside style={{ ...railStyle, width: COLLAPSED_WIDTH }}>
        <button
          type="button"
          aria-label="Expand node palette"
          title="Expand node palette"
          onClick={() => setCollapsed(false)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '100%', padding: `${SPACING.m}px 0`,
            background: 'transparent', border: 'none',
            color: S[600], cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <ChevronsRight size={14} aria-hidden="true" />
        </button>
      </aside>
    );
  }

  // ── FULL PANEL ─────────────────────────────────────────────────
  return (
    <aside style={{ ...railStyle, width: PANEL_WIDTH }} aria-label="Node palette">
      {/* Hide the body scrollbar globally for the v4-no-scrollbar class.
        * Cross-browser: Firefox uses scrollbar-width, Chrome/Safari use
        * the ::-webkit-scrollbar pseudo. Wheel/touch scroll still works. */}
      <style>{`
        .v4-no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .v4-no-scrollbar::-webkit-scrollbar { display: none; width: 0; height: 0; }
      `}</style>

      {/* Search row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACING.xs,
        padding: `${SPACING.s + 1}px ${SPACING.s + 2}px`,
        borderBottom: `1px solid ${S[200]}`,
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={12} aria-hidden="true" style={{
            position: 'absolute', left: 7, top: '50%',
            transform: 'translateY(-50%)', color: S[400],
            pointerEvents: 'none',
          }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search nodes…"
            aria-label="Search nodes"
            style={{
              width: '100%',
              padding: '5px 8px 5px 24px',
              fontSize: FONT.sm, color: S[900],
              background: '#ffffff',
              border: `1px solid ${S[200]}`,
              borderRadius: RADIUS.md,
              outline: 'none', fontFamily: 'inherit',
            }}
          />
        </div>
        <button
          type="button"
          aria-label="Collapse node palette"
          title="Collapse node palette"
          onClick={() => setCollapsed(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 26, height: 26, padding: 0,
            background: 'transparent',
            border: `1px solid ${S[200]}`,
            borderRadius: RADIUS.md,
            color: S[600], cursor: 'pointer', fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          <ChevronsLeft size={12} aria-hidden="true" />
        </button>
      </div>

      {/* Tab strip — icon-only category tabs. Wraps into multiple rows
        * instead of horizontal scrolling (operator feedback: scroll bar
        * looked amateurish on a 9-icon strip in a 240px column). Each
        * row fits ~7 icons at 28px wide; with 9 categories that means
        * 2 rows. Hidden when a search is active (search bypasses the
        * category filter and shows matches across every category). */}
      {!search.trim() && (
        <div
          role="tablist"
          aria-label="Node categories"
          style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap',
            gap: 0,
            padding: `${SPACING.xs}px ${SPACING.xs + 2}px`,
            borderBottom: `1px solid ${S[200]}`,
          }}
        >
          {NODE_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.icon] || PlayCircle;
            const active = cat.id === activeCatId;
            return (
              <button
                key={cat.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setActiveCatId(cat.id)}
                title={cat.label}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  width: 30, height: 28,
                  padding: 0,
                  background: active ? BRAND.light : 'transparent',
                  color: active ? BRAND.dark : S[500],
                  border: 'none',
                  borderRadius: RADIUS.sm,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 160ms ease',
                }}
              >
                <Icon size={14} aria-hidden="true" />
              </button>
            );
          })}
        </div>
      )}

      {/* Body — single category in tab mode, or grouped matches in
        * search mode. Scrollbar is hidden (operator feedback: visible
        * scrollbar looked amateurish in the narrow rail). Wheel/touch
        * scrolling still works — only the bar is hidden via the
        * .v4-no-scrollbar class injected in the styles below. */}
      <div className="v4-no-scrollbar" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {buckets.length === 0 && (
          <div style={{
            padding: SPACING.xl, textAlign: 'center',
            fontSize: FONT.sm, color: S[500],
          }}>
            {search.trim()
              ? `No nodes match "${search.trim()}".`
              : 'No nodes in this category.'}
          </div>
        )}
        {buckets.map(({ category, nodes }) => (
          <section key={category.id}>
            {/* Category header — uppercase, subtle. Always rendered;
              * in tab mode it just confirms the active category, in
              * search mode it groups matches by category. */}
            <header style={categoryHeaderStyle}>
              <span style={{
                fontSize: FONT.xs, fontWeight: WEIGHT.semibold,
                color: S[500],
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {category.label}
              </span>
              <span style={{
                fontSize: FONT.xs, color: S[400],
                fontFeatureSettings: '"tnum"',
              }}>
                {nodes.length}
              </span>
            </header>
            <ul style={{ listStyle: 'none', margin: 0, padding: `0 0 ${SPACING.s}px` }}>
              {nodes.map((def) => (
                <li key={def.type}>
                  <NodeRow
                    def={def}
                    category={category}
                    readOnly={readOnly}
                    onClick={() => {
                      if (readOnly || typeof onAddOrphan !== 'function') return;
                      onAddOrphan(def.type);
                    }}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </aside>
  );
}

/* ───── single node row — 26px tall, icon + label only ─────────── */
function NodeRow({ def, category, readOnly, onClick }) {
  const [hovered, setHovered] = useState(false);
  const Icon = NODE_ICONS[def.icon] || CATEGORY_ICONS[category.icon] || PlayCircle;
  return (
    <div
      role="button"
      tabIndex={readOnly ? -1 : 0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (readOnly) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (typeof onClick === 'function') onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={def.description || def.label}
      style={{
        display: 'flex', alignItems: 'center',
        gap: SPACING.s,
        padding: `4px ${SPACING.m}px`,
        background: hovered ? BRAND.light : 'transparent',
        cursor: readOnly ? 'not-allowed' : 'grab',
        opacity: readOnly ? 0.5 : 1,
        transition: 'background 120ms ease',
      }}
    >
      <Icon
        size={14}
        aria-hidden="true"
        style={{
          color: hovered ? BRAND.dark : (category.color || S[600]),
          flexShrink: 0,
        }}
      />
      <span style={{
        fontSize: FONT.md,
        color: hovered ? BRAND.dark : S[900],
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        flex: 1,
      }}>
        {def.label}
      </span>
    </div>
  );
}

/* ───── shared styles ────────────────────────────────────────── */

const railStyle = {
  display: 'flex', flexDirection: 'column',
  height: '100%',
  background: '#ffffff',
  borderRight: `1px solid ${S[200]}`,
  flexShrink: 0,
  fontFamily: 'inherit',
};

const categoryHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: SPACING.s,
  padding: `${SPACING.s}px ${SPACING.m}px ${SPACING.xs}px`,
};
